import { useTranslation } from 'react-i18next';
import type { SalleKey } from './tableState';
import type { WaiterArea, WaiterTable } from './waiterApi';

interface Props {
  areas: WaiterArea[];
  tables: WaiterTable[];
  selected: SalleKey;
  onSelect: (key: SalleKey) => void;
}

/**
 * Spec 2026-08-28-waiter-salle-ux-design.md §2.2 D1 — the salle chip bar
 * sitting above `TableStateFilter`, `sticky top-0` inside the Sur place
 * tabpanel (`NotesListPage.tsx` wires the sticky wrapper). Read-only: the
 * handheld picks which salle it looks at, it never creates or renames one.
 * D4's `Gérer` mode was removed — a table and a salle are restaurant
 * configuration, edited in the admin / owner dashboards only. With zero
 * areas there is nothing left to show, so the bar renders nothing and gives
 * its ~60px back to the table grid (superseding review F1F2#1's "always
 * renders", which existed solely to keep `Gérer` reachable).
 */
export default function SalleBar({ areas, tables, selected, onSelect }: Props) {
  const { t } = useTranslation();
  const hasUnassigned = tables.some((tb) => !tb.area);

  if (areas.length === 0) return null;

  return (
    <div
      data-testid="salle-bar"
      className="flex items-center gap-2 px-3 py-2 border-b border-hairline bg-paper overflow-x-auto"
      // live-review finding m2 — a 24px right-edge fade so a chip cut by
      // the scroller's edge (e.g. "Terrasse" mid-word) reads as "more to
      // the right" instead of looking like the end of the list.
      style={{
        maskImage: 'linear-gradient(to right, black calc(100% - 24px), transparent)',
        WebkitMaskImage: 'linear-gradient(to right, black calc(100% - 24px), transparent)',
      }}
    >
      <button
        type="button"
        data-testid="salle-chip-all"
        aria-pressed={selected === 'all'}
        onClick={() => onSelect('all')}
        className={`shrink-0 h-11 px-3 rounded-full border-2 font-sans text-base font-semibold ${
          selected === 'all' ? 'border-ink bg-ink text-paper' : 'border-ink bg-paper text-ink'
        }`}
      >
        {t('waiter.notes.salle.all')}
      </button>
      {areas.map((area) => {
        const isSelected = selected === area.uuid;
        return (
          <button
            key={area.uuid}
            type="button"
            data-testid={`salle-chip-${area.uuid}`}
            aria-pressed={isSelected}
            onClick={() => onSelect(area.uuid)}
            className={`shrink-0 h-11 px-3 flex items-center gap-1.5 rounded-full border-2 font-sans text-base font-semibold ${
              isSelected ? 'border-ink bg-ink text-paper' : 'border-ink bg-paper text-ink'
            }`}
          >
            {area.color && (
              <span
                data-testid="salle-chip-dot"
                aria-hidden="true"
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: area.color }}
              />
            )}
            <span className="truncate max-w-[8rem]">{area.name}</span>
          </button>
        );
      })}
      {hasUnassigned && (
        <button
          type="button"
          data-testid="salle-chip-none"
          aria-pressed={selected === 'none'}
          onClick={() => onSelect('none')}
          className={`shrink-0 h-11 px-3 rounded-full border-2 font-sans text-base font-semibold ${
            selected === 'none' ? 'border-ink bg-ink text-paper' : 'border-ink bg-paper text-ink'
          }`}
        >
          {t('waiter.notes.salle.none')}
        </button>
      )}
    </div>
  );
}
